import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, X, Sparkles, Brain, Moon, Wind, Waves, Compass } from 'lucide-react';
import { getAllPosts } from '../data/mockData';
import { manifestThemes } from '../data/manifestData';

/**
 * UniversalSearchPage — one input, everything reachable.
 * Indexes: meditations, reflections, affirmations, manifest themes,
 *          breathing exercises, and category pages. Everything is
 *          client-side / synchronous so search is instant.
 */

const KIND_META = {
  post:      { icon: Brain,    label: 'Meditation',  accent: '#4a7c65' },
  sleep:     { icon: Moon,     label: 'Sleep',       accent: '#1e3a5f' },
  affirm:    { icon: Sparkles, label: 'Affirmation', accent: '#D6AD60' },
  theme:     { icon: Sparkles, label: 'Manifest',    accent: '#B8934D' },
  breathing: { icon: Wind,     label: 'Breathing',   accent: '#7a9b8e' },
  soundscape:{ icon: Waves,    label: 'Soundscape',  accent: '#5B7A65' },
  category:  { icon: Compass,  label: 'Category',    accent: '#8a7e60' },
};

const buildIndex = () => {
  const rows = [];
  // Posts (meditations, sleep, affirmations, anxiety)
  for (const p of getAllPosts()) {
    const kind = p.category === 'sleep' ? 'sleep'
               : p.category === 'affirmations' ? 'affirm'
               : 'post';
    rows.push({
      id: `post:${p.id}`,
      kind,
      title: p.title,
      subtitle: p.excerpt || (p.category || '').replace(/-/g, ' '),
      to: `/post/${p.id}`,
      image: p.image,
      // Searchable text bag — flatten to lowercase once.
      hay: [p.title, p.excerpt, p.category, ...(p.tags || [])].filter(Boolean).join(' ').toLowerCase(),
    });
  }
  // Manifest themes + their affirmations
  for (const theme of manifestThemes) {
    rows.push({
      id: `theme:${theme.id}`,
      kind: 'theme',
      title: theme.title,
      subtitle: theme.tagline || 'Manifest theme',
      to: `/manifest/${theme.id}`,
      image: (theme.images && theme.images[0]) || null,
      hay: [theme.title, theme.tagline, theme.description].filter(Boolean).join(' ').toLowerCase(),
    });
    (theme.affirmations || []).forEach((a, idx) => {
      rows.push({
        id: `aff:${theme.id}:${idx}`,
        kind: 'affirm',
        title: a.title,
        subtitle: `${theme.title} · affirmation`,
        to: `/a/${theme.id}/${idx}`,
        image: null,
        hay: [a.title, a.body, theme.title].filter(Boolean).join(' ').toLowerCase(),
      });
    });
  }
  return rows;
};

const UniversalSearchPage = () => {
  const [params] = useSearchParams();
  const inputRef = useRef(null);
  const [query, setQuery] = useState(params.get('q') || '');

  // Build the index once per mount — content is static.
  const index = useMemo(() => buildIndex(), []);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    // Split query into tokens; every token must appear in the hay.
    const tokens = q.split(/\s+/).filter(Boolean);
    const hits = index.filter((row) => tokens.every((t) => row.hay.includes(t)));
    // Rank: exact title matches first, then title prefix, then rest.
    hits.sort((a, b) => {
      const at = a.title.toLowerCase();
      const bt = b.title.toLowerCase();
      const ax = at === q ? 0 : at.startsWith(q) ? 1 : at.includes(q) ? 2 : 3;
      const bx = bt === q ? 0 : bt.startsWith(q) ? 1 : bt.includes(q) ? 2 : 3;
      return ax - bx;
    });
    return hits.slice(0, 40);
  }, [query, index]);

  const suggestions = ['sleep', 'anxiety', 'grief', 'self-worth', 'breathe', 'gratitude', 'let go'];

  return (
    <div className="pb-32 min-h-screen bg-[#F5EFE0]" data-testid="universal-search-page">
      {/* Search bar */}
      <div className="sticky top-0 z-20 px-4 pt-4 pb-3 bg-[#F5EFE0]/95 backdrop-blur-md border-b border-stone-200/60">
        <div className="relative">
          <Search className="w-4 h-4 text-stone-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search meditations, affirmations, breathing…"
            className="w-full pl-11 pr-11 py-3 rounded-2xl bg-[#FFFCF3] border border-stone-200 focus:border-[#D6AD60]/60 focus:outline-none text-[14px] text-stone-800 placeholder:text-stone-400 shadow-sm"
            data-testid="universal-search-input"
            maxLength={80}
            enterKeyHint="search"
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(''); inputRef.current?.focus(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[#E8DDCB] hover:bg-[#DBC9A3] flex items-center justify-center"
              data-testid="universal-search-clear"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5 text-stone-600" />
            </button>
          )}
        </div>
      </div>

      {/* Suggestions when empty */}
      {query.trim().length < 2 && (
        <div className="px-5 py-6">
          <p className="rbr-eyebrow mb-3 text-stone-500">Try one of these</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setQuery(s)}
                data-testid={`universal-search-suggest-${s}`}
                className="px-3 py-1.5 rounded-full bg-[#FFFCF3] border border-stone-200 text-[12px] text-stone-700 hover:border-[#D6AD60]/60 hover:text-stone-900 transition-all active:scale-95"
              >
                {s}
              </button>
            ))}
          </div>
          <p className="text-[12px] text-stone-500 mt-8 text-center max-w-xs mx-auto leading-relaxed">
            Type at least 2 letters. We&rsquo;ll search every reflection,
            affirmation, and breath in the app.
          </p>
        </div>
      )}

      {/* Results */}
      {query.trim().length >= 2 && (
        <div className="px-4 py-4">
          {results.length === 0 ? (
            <div className="text-center py-10 px-4">
              <p className="text-sm text-stone-800 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Nothing matched &ldquo;{query.trim()}&rdquo;
              </p>
              <p className="text-[12px] text-stone-500">Try a simpler word or a feeling.</p>
            </div>
          ) : (
            <>
              <p className="rbr-eyebrow mb-3 text-stone-500">
                {results.length} result{results.length === 1 ? '' : 's'}
              </p>
              <div className="flex flex-col gap-2">
                {results.map((r) => {
                  const meta = KIND_META[r.kind] || KIND_META.post;
                  const Icon = meta.icon;
                  return (
                    <Link
                      key={r.id}
                      to={r.to}
                      data-testid={`universal-search-hit-${r.id}`}
                      className="flex items-center gap-3 p-3 rounded-2xl bg-[#FFFCF3] border border-stone-200 hover:border-[#D6AD60]/40 hover:shadow-sm transition-all active:scale-[0.99]"
                    >
                      {r.image ? (
                        <img
                          src={r.image}
                          alt=""
                          className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ background: `linear-gradient(135deg, ${meta.accent} 0%, ${meta.accent}cc 100%)` }}
                        >
                          <Icon className="w-5 h-5" style={{ color: '#FFFCF3' }} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-[14px] font-semibold text-stone-800 truncate leading-tight"
                          style={{ fontFamily: "'Playfair Display', serif" }}
                        >
                          {r.title}
                        </p>
                        <p className="text-[11px] text-stone-500 truncate mt-0.5 capitalize">
                          <span className="uppercase tracking-wider text-[10px] font-semibold" style={{ color: meta.accent }}>
                            {meta.label}
                          </span>
                          <span className="text-stone-300 mx-1.5">·</span>
                          {r.subtitle}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default UniversalSearchPage;
